import type { CSSProperties } from 'react';

/**
 * The brand gradient as a PROGRESS RAMP — near 0% at the cyan end, travelling
 * toward the far end as a reading approaches 100%.
 *
 * ⚠️⚠️ This is the ONE place the ramp is applied. The spectral gradient is
 * otherwise reserved for filled buttons and the XP/orb signature, and
 * `theme.css` is its single source of truth — so nothing here names a colour.
 * Both helpers window `var(--progress-ramp)` with `background-size`, which
 * means restopping the gradient in CSS restops every bar and every day cell,
 * and an off-brand theme (where the var is a flat accent) keeps its own hue for
 * free.
 *
 * ⭐ THE TRICK, once: the ramp is painted at the size of the WHOLE track and
 * then clipped, rather than squeezed into whatever the fill happens to be. A
 * squeezed gradient shows all three stops at every value, so a 10% bar and a
 * 90% bar would end in the same orange and the ramp would say nothing. Windowed,
 * the colour a reading ENDS on is the reading.
 */

/** `background-size` for a sample window ~1% wide — near enough to a flat colour. */
const SAMPLE_ZOOM = 10000;

/** A reading at or above this wears the completion mark instead of its number. */
export const COMPLETE = 100;

/** 0–100, with null (nothing to measure) and any stray NaN landing on 0. */
function clampPct(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/**
 * Style for a bar's FILL element — width included, so every bar in the app
 * agrees about what "40%" looks like.
 *
 * @param value the reading, 0–100; null renders nothing
 * @param color an explicit colour that OVERRIDES the ramp. ⚠️ Domain surfaces
 *   pass their domain hue: the theme rule is "gradient unless a colour is
 *   defined, then the domain colour wins", and seven cards that all ended on
 *   the same far-end colour would stop saying which domain they were.
 */
export function rampBarFill(value: number | null, color?: string): CSSProperties {
  const pct = clampPct(value);
  if (color) return { width: `${pct}%`, backgroundColor: color };
  return {
    width: `${pct}%`,
    backgroundImage: 'var(--progress-ramp)',
    // ⭐ The fill is `pct%` of the track, so a background `100/pct` times the
    // FILL's width is exactly the TRACK's width — the ramp laid across the
    // whole road, with the fill showing only how far it has been walked.
    backgroundSize: `${pct > 0 ? (100 / pct) * 100 : 100}% 100%`,
    backgroundRepeat: 'no-repeat',
  };
}

/**
 * Style for a small cell that should read as ONE colour — the ramp sampled at
 * `value`. Used by the day grid, where a gradient inside a 12px square would
 * be noise rather than information.
 *
 * ⚠️ Carries no opacity: the day grid dims its own cells, because dimming is
 * how "missed" stays distinct from "kept" and neither may be confused with the
 * dashed "nothing was due".
 */
export function rampSample(value: number | null, color?: string): CSSProperties {
  const pct = clampPct(value);
  if (color) return { backgroundColor: color };
  return {
    backgroundImage: 'var(--progress-ramp)',
    // A window ~1% wide parked `pct` along the ramp. `background-position: P%`
    // aligns the image's P% point with the box's P% point, so with an image
    // this much wider than the box, P is simply the position on the ramp.
    backgroundSize: `${SAMPLE_ZOOM}% 100%`,
    backgroundPosition: `${pct}% 0`,
    backgroundRepeat: 'no-repeat',
  };
}

/**
 * Style for a SLICE of the ramp — the window from `fromPct` to `toPct`, drawn
 * across the box as its own little gradient.
 *
 * ⭐ Added for the wizard's step indicator: each node wears `rampSample` at its
 * own point, and the connector between two nodes wears the ramp BETWEEN those
 * two points — so the row reads as one gradient split across the elements
 * rather than the whole gradient repeated inside each of them.
 *
 * ⚠️ Same windowing trick as `rampSample`, which is the 1%-wide case of this.
 * With `background-size: S%` and `background-position: P%`, the visible slice
 * is `1/s` of the image starting at `(P/100)·(s-1)/s` (where `s = S/100`).
 * Solving for a window of `[f, t]` gives `s = 1/(t-f)` and
 * `P = 100·f / (1-(t-f))`.
 */
export function rampSegment(fromPct: number, toPct: number): CSSProperties {
  const f = clampPct(fromPct) / 100;
  const t = clampPct(toPct) / 100;
  const width = t - f;
  // ⚠️ A zero-width window has no gradient to show and a FULL-width one needs
  // no windowing — and both divide by zero in the formula above.
  if (width <= 0) return rampSample(fromPct);
  if (width >= 1) {
    return { backgroundImage: 'var(--progress-ramp)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' };
  }
  return {
    backgroundImage: 'var(--progress-ramp)',
    backgroundSize: `${(1 / width) * 100}% 100%`,
    backgroundPosition: `${(100 * f) / (1 - width)}% 0`,
    backgroundRepeat: 'no-repeat',
  };
}

/**
 * Style for a donut's ARC — the same sampled colour as a day cell, cut to a
 * `value`-long sweep from twelve o'clock.
 *
 * ⭐ The colour comes from `rampSample`, so a donut and a day cell reading the
 * same number are the same colour. The sweep is a conic-gradient MASK rather
 * than a conic-gradient background, which is what lets the colour stay a ramp
 * sample instead of being re-specified as a literal — nothing here names a
 * colour either.
 *
 * ⚠️ Caller draws the track under it and the hole over it. Compositing two
 * masks (`mask-composite`) would punch the hole here, but support is uneven
 * across the WebKit this ships into on iOS — two plain elements always work.
 */
export function rampRing(value: number | null, color?: string): CSSProperties {
  const pct = clampPct(value);
  // `#000 P%, transparent 0` — the second stop's `0` is CSS shorthand for
  // "same position as the previous one", giving a hard edge rather than a fade.
  const sweep = `conic-gradient(#000 ${pct}%, transparent 0)`;
  return { ...rampSample(value, color), WebkitMaskImage: sweep, maskImage: sweep };
}
